const THIRD_PARTY_PREFIXES = ["insurance_agent", "title_agent", "borrower_attorney"] as const;

type ThirdPartyPrefix = (typeof THIRD_PARTY_PREFIXES)[number];

const THIRD_PARTY_CONTACT_KEY_BY_PREFIX: Record<ThirdPartyPrefix, string> = {
    insurance_agent: "insuranceAgent",
    title_agent: "titleAgent",
    borrower_attorney: "borrowerAttorney",
};

const THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP = {
    contact_first_name: "firstName",
    contact_last_name: "lastName",
    email: "email",
    phone: "phone",
} as const;

type ThirdPartyContactProp = (typeof THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP)[keyof typeof THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP];

function getThirdPartyPrefix(fieldId: string): ThirdPartyPrefix | undefined {
    return THIRD_PARTY_PREFIXES.find((prefix) => fieldId.startsWith(`${prefix}_`));
}

function getFieldSuffix(fieldId: string, prefix: ThirdPartyPrefix): string {
    return fieldId.slice(prefix.length + 1);
}

function getThirdPartyContactFromRawLoanData(rawLoanData: Record<string, any>, prefix: ThirdPartyPrefix): Record<string, any> | undefined {
    const thirdPartyInformation = rawLoanData?.thirdPartyInformation;
    if (!thirdPartyInformation || typeof thirdPartyInformation !== "object") {
        return undefined;
    }

    const contactKey = THIRD_PARTY_CONTACT_KEY_BY_PREFIX[prefix];
    const contact = (thirdPartyInformation as Record<string, any>)[contactKey];
    if (!contact || typeof contact !== "object" || Array.isArray(contact)) {
        return undefined;
    }

    return contact as Record<string, any>;
}

export function readThirdPartyReportField(rawLoanData: Record<string, any>, fieldId: string): string | undefined {
    const prefix = getThirdPartyPrefix(fieldId);
    if (!prefix) return undefined;

    const suffix = getFieldSuffix(fieldId, prefix);
    const prop = THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP[
        suffix as keyof typeof THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP
    ] as ThirdPartyContactProp | undefined;

    if (!prop) return "";

    const contact = getThirdPartyContactFromRawLoanData(rawLoanData, prefix);
    const value = contact?.[prop];
    if (value === undefined || value === null) return "";
    return String(value);
}

export function writeThirdPartyReportField(
    rawLoanData: Record<string, any>,
    fieldId: string,
    value: string
): boolean {
    const prefix = getThirdPartyPrefix(fieldId);
    if (!prefix) return false;

    const suffix = getFieldSuffix(fieldId, prefix);
    const prop = THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP[
        suffix as keyof typeof THIRD_PARTY_FIELD_SUFFIX_TO_CONTACT_PROP
    ] as ThirdPartyContactProp | undefined;

    if (!prop) return true;

    const thirdPartyInformation = ((rawLoanData.thirdPartyInformation ??= {}) as Record<string, any>);
    const contactKey = THIRD_PARTY_CONTACT_KEY_BY_PREFIX[prefix];
    const contact = ((thirdPartyInformation[contactKey] ??= {}) as Record<string, any>);
    contact[prop] = value || "";
    return true;
}
